import { 
  CreateSupportTicketRequest, 
  SupportTicket, 
  SupportTicketCreateResponse, 
  SupportCategoryInfo 
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Callback function to clear auth state on 401 errors
let onUnauthorized: (() => void) | null = null;

export const setSupportApiUnauthorizedCallback = (callback: () => void) => {
  onUnauthorized = callback;
};

class SupportApiService {
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Get token from localStorage for authenticated requests
    const token = localStorage.getItem('auth-store');
    let authToken = null;
    
    if (token) {
      try {
        const parsed = JSON.parse(token);
        authToken = parsed.state?.token;
      } catch (error) {
        console.error('Failed to parse auth token:', error);
      }
    }

    if (authToken) {
      defaultHeaders['Authorization'] = `Bearer ${authToken}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      // Handle unauthorized responses
      if (response.status === 401) {
        if (onUnauthorized) {
          onUnauthorized();
        }
        throw new Error('Unauthorized access');
      }
      
      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          // If we can't parse error response, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Handle responses that might not have JSON content
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        // Return response status for non-JSON responses
        return { success: true, status: response.status } as any;
      }
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // Create a new support ticket
  async createTicket(ticketData: CreateSupportTicketRequest): Promise<SupportTicketCreateResponse> {
    // Add browser info automatically
    const requestData = {
      ...ticketData,
      userAgent: navigator.userAgent,
      currentUrl: window.location.href
    };

    return this.makeRequest<SupportTicketCreateResponse>('/support/ticket', {
      method: 'POST',
      body: JSON.stringify(requestData),
    });
  }

  // Get all user's support tickets
  async getUserTickets(): Promise<SupportTicket[]> {
    return this.makeRequest<SupportTicket[]>('/support/tickets');
  }

  // Get a specific ticket by ID
  async getTicketById(ticketId: string): Promise<SupportTicket> {
    return this.makeRequest<SupportTicket>(`/support/tickets/${ticketId}`);
  }

  // Get support categories for dropdown
  async getSupportCategories(): Promise<SupportCategoryInfo[]> {
    return this.makeRequest<SupportCategoryInfo[]>('/support/categories');
  }

  // Get user's open ticket count
  async getUserOpenTicketCount(): Promise<number> {
    return this.makeRequest<number>('/support/tickets-count');
  }
}

// Export singleton instance
export const supportApi = new SupportApiService();
